import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

const REQUEST_SCHEME = "gw-request:";
const INTAKE_SCHEME = "gw-intake:";

interface Props {
  text: string;
  /** Called when a citation chip is clicked, with the app path to open: `/requests/<id>` or `/intake/<source>/<id>`. */
  onCitation?: (path: string) => void;
}

/** The app path behind a citation href, or null when it is not a citation. */
export function citationPath(href: string | undefined): string | null {
  if (href?.startsWith(REQUEST_SCHEME)) return `/requests/${encodeURIComponent(href.slice(REQUEST_SCHEME.length))}`;
  if (href?.startsWith(INTAKE_SCHEME)) {
    const [source, ...rest] = href.slice(INTAKE_SCHEME.length).split("/");
    const id = rest.join("/");
    return source && id ? `/intake/${encodeURIComponent(source)}/${encodeURIComponent(id)}` : null;
  }
  return null;
}

/**
 * Renders assistant text as markdown (GFM: tables, lists, code, bold). react-markdown does not
 * render raw HTML unless a rehype-raw plugin is added, so this is XSS-safe by default. Links with
 * the `gw-request:<id>` or `gw-intake:<source>/<id>` scheme render as clickable citation chips
 * (via `onCitation`, with the app path); other links open in a new tab. Used for assistant messages only; user messages render as plain text.
 */
export function MarkdownMessage({ text, onCitation }: Props) {
  return (
    <div className="chat-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // react-markdown sanitizes non-standard URL schemes by default; let our citation scheme
        // through so the custom `a` renderer below can pick it up (everything else stays sanitized).
        urlTransform={(url) => (citationPath(url) ? url : defaultUrlTransform(url))}
        components={{
          a: ({ href, children, ...props }) => {
            const path = citationPath(href);
            if (path) {
              return (
                <button
                  type="button"
                  className="chat-citation"
                  onClick={() => onCitation?.(path)}
                  title={path.startsWith("/intake/") ? "Open this intake" : "Open this request"}
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 13 }}>
                    open_in_new
                  </span>
                  {children}
                </button>
              );
            }
            return (
              <a href={href} {...props} target="_blank" rel="noreferrer noopener">
                {children}
              </a>
            );
          },
          // Wide tables scroll horizontally inside the message instead of widening the panel.
          table: ({ ...props }) => (
            <div className="chat-table-wrap">
              <table {...props} />
            </div>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
